import React, { useEffect, useState } from 'react';
import { Animated, Modal } from 'react-native';
import { Spinner } from '@/components/ui/Spinner';
import { ScaledSheet } from '@/theme/responsive';

interface Props {
  visible: boolean;
}

/**
 * Full-screen blocking overlay with the app's spinner in the centre.
 * Rendered as a transparent modal so it sits above absolutely everything
 * (keyboard, navigation bars) and swallows all touch events.
 *
 * Usage:
 *   const isSubmitting = useAppSelector(s => s.auth.isSubmitting);
 *   <FullScreenLoader visible={isSubmitting} />
 */
export function FullScreenLoader({ visible }: Props) {
  const [scale] = useState(() => new Animated.Value(0.8));
  const [opacity] = useState(() => new Animated.Value(0));

  useEffect(() => {
    if (visible) {
      // Fade + scale in
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 220, useNativeDriver: true }),
        Animated.spring(scale, { toValue: 1, friction: 5, useNativeDriver: true }),
      ]).start();
    } else {
      // Reset for next use
      opacity.setValue(0);
      scale.setValue(0.8);
    }
  }, [visible, opacity, scale]);

  return (
    <Modal
      transparent
      visible={visible}
      animationType="none"
      statusBarTranslucent
      // Modal absorbs all pointer events while visible
      onRequestClose={() => {}}
    >
      <Animated.View style={[styles.backdrop, { opacity }]}>
        <Animated.View style={{ transform: [{ scale }] }}>
          <Spinner size={52} color="#FFFFFF" />
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}

const styles = ScaledSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(15, 15, 30, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
